import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiShield, FiBook, FiFileText, FiCheckCircle, FiAlertTriangle, FiUser, FiActivity, FiClock, FiTarget, FiLayers, FiTrendingUp, FiDatabase, FiCpu } from 'react-icons/fi';
import { jsPDF } from 'jspdf';

const DefensivePublicationJournal = () => {
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;
    
    // Header with branding
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Snap Chart AI - Prior Art Documentation', margin, 22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Defensive Publication - Trading Journal', margin, 30);
    
    yPosition = 50;
    doc.setTextColor(0, 0, 0);
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Defensive Publication - Trading Journal', margin, yPosition);
    yPosition += 10;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const subtitle = doc.splitTextToSize('AI-Based Institutional Trading Journal with Behavioral Memory, Rule-Adherence Scoring, and Pre/Post-Trade Governance', maxWidth);
    doc.text(subtitle, margin, yPosition);
    yPosition += subtitle.length * 6 + 8;
    
    // Metadata
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Applicant / Publisher:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Snap-Chart AI (https://snap-chart.ai/)', margin + 40, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Product Name:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Trading Journal (Institutional Trading Journal)', margin + 30, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Publication Date:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('December 14th, 2025', margin + 35, yPosition);
    yPosition += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Jurisdiction:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Global (Defensive Prior Art)', margin + 25, yPosition);
    yPosition += 12;
    
    // Abstract
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Abstract', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const abstractText = doc.splitTextToSize(
      'This publication describes systems and methods for an AI-assisted institutional trading journal designed to capture, structure, and govern a trader\'s decisions across the full lifecycle of a trade (pre-trade plan → execution → post-trade review). The system emphasizes discipline, repeatability, and capital protection by transforming unstructured trader inputs (text, voice, screenshots, and context) into standardized journal entries, behavioral patterns, and compliance-to-plan feedback. The system is not a signal generator and does not predict markets; it focuses on documenting intent, enforcing process integrity, and improving decision quality over time.',
      maxWidth
    );
    doc.text(abstractText, margin, yPosition);
    yPosition += abstractText.length * 4 + 8;
    
    // Field of the Invention
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Field of the Invention', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const fields = [
      '• Financial technology (FinTech)',
      '• Artificial intelligence applied to trading process governance',
      '• Behavioral finance and trading discipline instrumentation',
      '• Structured journaling for discretionary and systematic traders',
      '• Human-in-the-loop decision documentation and review systems'
    ];
    fields.forEach(field => {
      doc.text(field, margin, yPosition);
      yPosition += 5;
    });
    yPosition += 5;
    
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    // Background
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Background', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const backgroundText = doc.splitTextToSize(
      'Most journaling tools focus on post-trade recordkeeping (entry, exit, P/L), screenshots, and statistics. Institutional-grade trading operations, however, require a repeatable process with documented pre-trade thesis, clear risk definition, post-trade accountability, and behavioral tracking. There is a need for a journal that functions as a process governance layer, transforming journaling into a discipline and accountability system—not merely a trade log.',
      maxWidth
    );
    doc.text(backgroundText, margin, yPosition);
    yPosition += backgroundText.length * 4 + 10;
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('© 2026 SnapChart AI. All rights reserved.', margin, 285);
    doc.text('This document constitutes defensive prior art publication.', margin, 290);
    
    doc.save('Snap-Chart-AI-Defensive-Publication-Trading-Journal.pdf');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-gray-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/legal/defensive-publications" 
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <FiArrowLeft className="text-lg" />
            <span>Back to Defensive Publications</span>
          </Link>
          <button
            onClick={generatePDF}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-lg text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-cyan-500/50 transition-all"
          >
            <FiDownload className="text-lg" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
              <FiBook className="text-2xl text-cyan-400" />
            </div>
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-full border border-cyan-500/30">
              Defensive Prior Art
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Defensive Publication - Trading Journal
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            AI-Based Institutional Trading Journal with Behavioral Memory, Rule-Adherence Scoring, and Pre/Post-Trade Governance
          </p>
        </div>

        {/* Metadata Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Applicant / Publisher</p>
            <p className="text-white font-medium">Snap-Chart AI</p>
            <a href="https://snap-chart.ai/" className="text-cyan-400 text-sm hover:underline">https://snap-chart.ai/</a>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Product Name</p>
            <p className="text-white font-medium">Trading Journal</p>
            <p className="text-gray-400 text-sm">Institutional Trading Journal</p>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Publication Date</p>
            <p className="text-white font-medium">December 14th, 2025</p>
          </div>
          <div className="p-4 bg-gray-800/50 rounded-xl border border-white/10">
            <p className="text-sm text-gray-500 mb-1">Jurisdiction</p>
            <p className="text-white font-medium">Global (Defensive Prior Art)</p>
          </div>
        </div>

        {/* Main Content */}
        <article className="prose prose-invert prose-lg max-w-none [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:bg-gradient-to-r [&_h2]:from-purple-400 [&_h2]:to-cyan-400 [&_h2]:bg-clip-text [&_h2]:text-transparent [&_h2]:mb-6 [&_h2]:mt-12 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-4 [&_h3]:mt-8 [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:space-y-4 [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6">
          
          <h2>Abstract</h2>
          <p>
            This publication describes systems and methods for an AI-assisted institutional trading journal designed to capture, structure, and govern a trader's decisions across the full lifecycle of a trade (pre-trade plan → execution → post-trade review). The system emphasizes discipline, repeatability, and capital protection by transforming unstructured trader inputs (text, voice, screenshots, and context) into standardized journal entries, behavioral patterns, and compliance-to-plan feedback. The system is not a signal generator and does not predict markets; it focuses on documenting intent, enforcing process integrity, and improving decision quality over time.
          </p>

          <h2>Field of the Invention</h2>
          <p>The present disclosure relates to:</p>
          <ul>
            <li>Financial technology (FinTech)</li>
            <li>Artificial intelligence applied to trading process governance</li>
            <li>Behavioral finance and trading discipline instrumentation</li>
            <li>Structured journaling for discretionary and systematic traders</li>
            <li>Human-in-the-loop decision documentation and review systems</li>
          </ul>

          <h2>Background</h2>
          <p>
            Most journaling tools focus on post-trade recordkeeping (entry, exit, P/L), screenshots, and statistics. Institutional-grade trading operations, however, require a repeatable process with:
          </p>
          <ul>
            <li>A documented pre-trade thesis and rule set</li>
            <li>Clear risk definition and invalidation logic</li>
            <li>Post-trade accountability vs. original plan</li>
            <li>Behavioral tracking (impulsivity, revenge trading, over-trading, rule drift)</li>
          </ul>
          <p>
            There is a need for a journal that functions as a process governance layer, transforming journaling into a discipline and accountability system—not merely a trade log.
          </p>

          <h2>Summary of the System (High-Level)</h2>
          <p>The system comprises an AI-driven journaling and governance engine that:</p>

          <h3>1) Captures multi-modal trader inputs</h3>
          <ul>
            <li>Pre-trade plan (setup, thesis, conditions, invalidation)</li>
            <li>Trade execution details (size, leverage, risk, R-multiple plan)</li>
            <li>Post-trade reflections (what happened, emotions, mistakes, lessons)</li>
            <li>Optional: voice notes, chat transcripts, screenshots, and timestamps</li>
          </ul>

          <h3>2) Normalizes entries into a structured institutional template</h3>
          <ul>
            <li>Trade intent record (why this trade exists)</li>
            <li>Rule checklist (what rules were satisfied / violated)</li>
            <li>Risk definition (max loss, stop logic, exposure)</li>
            <li>Execution trace (changes, edits, partials, exits)</li>
            <li>Outcome and review (variance vs plan, slippage vs plan, emotion markers)</li>
          </ul>

          <h3>3) Performs plan-compliance and discipline auditing</h3>
          <ul>
            <li>Detects deviations from the pre-trade plan (rule drift)</li>
            <li>Flags "impulse edits" (moving stops, doubling size, revenge entries)</li>
            <li>Tracks frequency anomalies (over-trading, rapid re-entries, session spirals)</li>
            <li>Produces a post-trade accountability summary (plan vs reality)</li>
          </ul>

          <h3>4) Builds behavioral memory over time</h3>
          <ul>
            <li>Aggregates repeated behaviors into patterns (e.g., "late entries after loss")</li>
            <li>Identifies "high-risk session states" (tilt risk, boredom trades, FOMO)</li>
            <li>Adapts feedback strictness and pacing recommendations based on history</li>
          </ul>

          <h3>5) Produces institutional-grade outputs</h3>
          <ul>
            <li>Journal entries formatted for consistency and auditability</li>
            <li>Periodic "discipline reports" (weekly/monthly)</li>
            <li>Pattern summaries and targeted corrective drills</li>
            <li>Optional integration with a risk-governance layer (approve / wait / no-trade)</li>
          </ul>

          <h2>Behavioral Intelligence Layer (Conceptual)</h2>
          <p>The system may derive a Trader Process Fingerprint, representing aggregated characteristics such as:</p>
          <ul>
            <li>Plan adherence consistency</li>
            <li>Impulsivity vs patience</li>
            <li>Reaction to wins/losses</li>
            <li>Frequency patterns and session stability</li>
            <li>"Rule drift" risk under stress</li>
          </ul>
          <p>This fingerprint may influence:</p>
          <ul>
            <li>The journal prompts the AI asks the trader</li>
            <li>The strictness of audit flags</li>
            <li>The recommended pacing (cooldowns, no-trade windows)</li>
          </ul>
          <p className="text-yellow-400/80 italic">
            Note: Specific scoring formulas, thresholds, and weighting are implementation-specific and intentionally undisclosed.
          </p>

          <h2>Decision Authority Model</h2>
          <p>The system:</p>
          <ul>
            <li>Does not provide trade signals</li>
            <li>Does not predict price movements</li>
            <li>Does not guarantee outcomes</li>
          </ul>
          <p>Instead, it:</p>
          <ul>
            <li>Documents decisions and intent with audit trails</li>
            <li>Evaluates discipline and plan adherence</li>
            <li>Recommends corrective action (review / pause / reduce risk)</li>
            <li>Supports process governance before and after execution</li>
          </ul>

          <h2>Human–Machine Interface</h2>
          <p>The system may include:</p>
          <ul>
            <li>Conversational journaling (chat)</li>
            <li>Voice-based journaling (speech-to-structured entry)</li>
            <li>Upload and tagging of chart screenshots</li>
            <li>Dashboard for process metrics (discipline, adherence, session stability)</li>
          </ul>

          <h2>Distinguishing Characteristics</h2>
          <p>Key differentiators include:</p>
          <ul>
            <li>Institutional "process-first" journaling (not just P/L tracking)</li>
            <li>Pre-trade intent capture + post-trade variance auditing</li>
            <li>Behavioral memory and pattern detection across sessions</li>
            <li>Standardized templates enabling audit-grade review</li>
            <li>Separation between journaling/governance vs prediction/signal generation</li>
          </ul>

          <h2>Intended Use Cases</h2>
          <ul>
            <li>Discretionary traders</li>
            <li>Prop traders and evaluation programs</li>
            <li>Trading teams and performance coaches</li>
            <li>Fund training environments</li>
            <li>Educational trading environments</li>
          </ul>

          <h2>Disclaimer</h2>
          <p>This publication intentionally omits:</p>
          <ul>
            <li>Model architectures</li>
            <li>Prompt structures</li>
            <li>Scoring algorithms</li>
            <li>Data schemas and storage design</li>
            <li>Threshold values and weighting logic</li>
            <li>Implementation details</li>
          </ul>
          <p className="text-yellow-400/80 italic">
            These elements remain protected as trade secrets.
          </p>

          <h2>Purpose of Publication</h2>
          <div className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl border border-purple-500/30 mt-6">
            <p className="text-lg text-white font-medium mb-0">
              This document is published as defensive prior art to prevent the patenting of the described concepts by third parties.
            </p>
          </div>
        </article>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-sm text-center">
            © 2026 SnapChart AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DefensivePublicationJournal;
